/* eslint-disable indent */
import styled, { css, keyframes } from 'styled-components';

import { media, BREAKPOINTS } from 'src/styles/breakpoints';

export const StyledTable = styled.div<{ $columns?: string; $isDark: boolean }>`
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  padding-bottom: 1rem;

  .header {
    order: 0;
    flex-shrink: 0;
    border-radius: 0.5rem;
    border: 0;
    display: grid;
    grid-template-columns: ${({ $columns }) => $columns || 'auto'};
    padding: 0.5rem 0;
    margin-bottom: 1rem;
    min-height: 2.75rem;
    box-sizing: border-box;
  }

  .cell {
    padding: 0.5rem;
    text-align: left;
    font-size: 0.875rem;
    transition: all 0.3s ease-in-out;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
    word-break: break-word;

    &.actions-cell {
      overflow: visible;
    }
  }

  .table-row {
    cursor: pointer;
    border-radius: 0.5rem;
    transition: background 0.2s;
    display: grid;
    grid-template-columns: ${({ $columns }) => $columns || 'auto'};
    padding: 0.5rem 0;
    align-items: center;

    &.skeleton-row {
      pointer-events: none;

      &:hover {
        background: transparent;
        color: inherit;
      }

      &:nth-child(even) {
        background: ${({ theme }) => theme.lightGrey};
      }
    }

    &:nth-child(even) {
      background: ${({ theme }) => theme.lightGrey};
    }

    &:hover {
      background: ${({ $isDark }) => ($isDark ? '#172A4F' : '#f1f1f1')};
      color: ${({ $isDark }) => ($isDark ? '#fff' : 'inherit')};
    }
  }

  /* Tablet landscape: horizontal scroll instead of cramped grid */
  @media (max-width: ${BREAKPOINTS.lg}px) and (min-width: ${BREAKPOINTS.md + 1}px) {
    display: block;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;

    .header,
    .table-row {
      min-width: 40rem;
    }
  }

  /* Mobile / tablet portrait: stacked card rows */
  ${media.md} {
    overflow-x: visible;
    padding-bottom: 0.5rem;
    gap: 0;

    .header {
      display: none;
    }

    .table-row {
      display: flex;
      flex-direction: column;
      min-width: 0;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      border: 1px solid ${({ theme }) => theme.grey};
      box-shadow: none;
      gap: 0;

      &.skeleton-row {
        pointer-events: none;
      }

      &:nth-child(even) {
        background: ${({ theme }) => theme.background};
      }

      &:hover {
        background: ${({ theme }) => theme.background};
        color: inherit;
      }
    }

    .cell {
      display: grid;
      grid-template-columns: minmax(5rem, 42%) minmax(0, 1fr);
      align-items: center;
      column-gap: 0.75rem;
      text-align: right;
      padding: 0.65rem 0;
      width: 100%;
      font-size: 0.8125rem;
      border-bottom: 1px solid ${({ theme }) => theme.grey};

      &:last-child {
        border-bottom: none;
      }

      &::before {
        content: attr(data-label);
        grid-column: 1;
        grid-row: 1;
        font-weight: 600;
        font-size: 0.75rem;
        text-transform: capitalize;
        text-align: left;
        color: ${({ theme }) => theme.midGrey};
        line-height: 1.35;
      }

      > * {
        grid-column: 2;
        grid-row: 1;
        justify-self: end;
        width: auto;
        max-width: 100%;
        min-width: 0;
        display: block;
      }
    }

    .skeleton-row .cell > * {
      width: 100%;
      max-width: 100%;
      justify-self: stretch;
    }

    .actions-cell {
      padding-top: 0.65rem;
      margin-top: 0.15rem;
      border-top: 1px solid ${({ theme }) => theme.grey};
      border-bottom: none;

      &:before {
        align-self: center;
      }
    }

    .cell:has(+ .actions-cell) {
      border-bottom: none;
    }
  }

  ${media.xs} {
    gap: 0.65rem;

    .table-row {
      padding: 0.65rem 0.75rem;
    }

    .cell {
      font-size: 0.8rem;
      gap: 0.5rem;

      &:before {
        max-width: 46%;
      }
    }
  }
`;

export const StyledHeaderCell = styled.div<{ $isSortable: boolean }>`
  display: flex;
  align-items: center;
  cursor: ${({ $isSortable }) => ($isSortable ? 'pointer' : 'default')};
  user-select: none;
  transition: all 0.2s ease;
  padding: 0.5rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.midGrey};
  min-width: 0;
  gap: 0.25rem;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &:active {
    ${({ $isSortable }) =>
      $isSortable &&
      `
      transform: scale(0.98);
    `}
  }
`;

export const SortIcon = styled.span`
  font-size: 0.7rem;
  margin-left: 0.25rem;
  transition: color 0.2s ease;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
  flex-shrink: 0;

  svg {
    width: 12px;
    height: 12px;
  }

  ${StyledHeaderCell}:hover & {
    color: #333;
  }
`;

const shimmer = keyframes`
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
`;

const tableRowIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const SkeletonBar = styled.div<{ $width?: string }>`
  height: 0.875rem;
  width: ${({ $width }) => $width || '72%'};
  max-width: 100%;
  border-radius: 0.375rem;
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.lightGrey} 0%,
    ${({ theme }) => theme.grey} 45%,
    ${({ theme }) => theme.lightGrey} 90%
  );
  background-size: 200% 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
`;

export const TableBodyWrap = styled.div<{
  $dimmed?: boolean;
  $reveal?: boolean;
}>`
  order: 1;
  display: flex;
  flex-direction: column;
  transition: opacity 0.3s ease;

  ${media.md} {
    gap: 0.75rem;
  }

  ${({ $dimmed }) =>
    $dimmed &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `}

  ${({ $reveal }) =>
    $reveal &&
    css`
      .table-row:not(.skeleton-row) {
        animation: ${tableRowIn} 0.34s ease both;
      }
    `}
`;

export const DetailFieldsList = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  border: 1px solid ${({ theme }) => theme.grey};
  border-radius: 0.75rem;
  overflow: hidden;
  background: ${({ theme }) => theme.background};
`;

export const DetailFieldCard = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 0.85rem 1rem;
  border-bottom: 1px solid ${({ theme }) => theme.grey};

  &:last-child {
    border-bottom: none;
  }
`;

export const DetailFieldBody = styled.div`
  min-width: 0;
  flex: 1;
`;

export const DetailFieldLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: ${({ theme }) => theme.midGrey};
  margin-bottom: 0.25rem;
`;

export const DetailFieldValue = styled.div`
  font-size: 0.9375rem;
  font-weight: 400;
  color: ${({ theme }) => theme.dark};
  word-break: break-word;
`;

